import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, inject, input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatBadgeModule } from '@angular/material/badge';
import { IconComponent } from '@program-suite/util-icons';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { DashboardService, FeatureService, InAppNotificationsStore, ProgramTiles, SiteConfigStore } from '@program-suite/data-common';
import { FeatureEnum } from '@program-suite/data-common/program';
import { AuthStore } from '@program-suite/data-auth';
import { ModalService, ModalType } from '../../services/modal.service';
import { NotificationsModalComponent } from '../modals/notifications-modal/notifications-modal.component';
import { SelectProgramModalComponent } from '../modals/select-program-modal/select-program-modal.component';

interface NavItems {
  name: string;
  route: string;
  type: string;
  icon: string;
  class?: string;
  action?: 'startQuote';
}

@Component({
  selector: 'lib-sidenav',
  imports: [CommonModule, MatBadgeModule, IconComponent, RouterLink, RouterLinkActive],
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidenavComponent implements OnInit {
  siteConfigStore = inject(SiteConfigStore);
  notificationsStore = inject(InAppNotificationsStore);
  userStore = inject(AuthStore);
  navItems: NavItems[] = [];
  authStore = inject(AuthStore);
  notificationIcon = 'notifications';
  logoutIcon = 'arrow-left-from-bracket';
  routes = input<Array<{ path: string }> | undefined>();
  iconSrc = input<string>('');
  logoSrc = input<string>('');
  router = inject(Router);
  featureService = inject(FeatureService);
  cdr = inject(ChangeDetectorRef);

  imageSrc = this.iconSrc() || this.siteConfigStore.siteConfig()?.iconUrl;
  notificationsCount = 0;
  usersInitials = computed(() => {
    const user = this.authStore.user();
    return user ? `${user.firstName?.charAt(0) || ''}${user.lastName?.charAt(0) || ''}` : '';
  });
  currentUser = computed(() => {
    const user = this.authStore.user();
    return user ? `${user.firstName || ''} ${user.lastName || ''}` : '';
  });

  constructor(
    private modalService: ModalService,
    private dashboardService: DashboardService,
  ) {}

  ngOnInit(): void {

    if (this.authStore.isLossRunProcessor()) {
      this.imageSrc = '/assets/branding/shasta-icon.png';
      this.navItems = [
        {
          name: 'Policies',
          route: 'policies',
          type: 'btn-side-nav',
          icon: 'shield-check',
        },
      ];
      return;
    }

    if(this.authStore.isAdmin()) {
      this.imageSrc = '/assets/branding/shasta-icon.png';

      this.navItems = [
        {
          name: 'Dashboard',
          route: 'dashboard',
          type: 'btn-side-nav',
          icon: 'table-columns',
        },
        {
          name: 'Pending Requests',
          route: 'pending-request',
          type: 'btn-side-nav',
          icon: 'alarm-clock',
        },
        {
          name: 'Start a Quote',
          route: this.authStore.isAdmin() ? 'quote' : 'quote/new',
          type: 'btn-small',
          icon: 'dollar',
        },
        {

          name: 'Submissions',
          route: 'submissions',
          type: 'btn-side-nav',
          icon: 'arrow-down-to-line',
        },
        {
          name: 'Policies',
          route: 'policies',
          type: 'btn-side-nav',
          icon: 'shield-check',
        },
        {
          name: 'Program Guidelines',
          route: 'program-guidelines',
          type: 'btn-side-nav',
          icon: 'list-check',
        },
        {
          name: 'Policy Forms',
          route: 'policy-forms',
          type: 'btn-side-nav',
          icon: 'table',
        },
        {
          name: 'Wholesale Settings',
          route: 'wholesalers',
          type: 'btn-side-nav',
          icon: 'boxes-stacked',
        },
        {
          name: 'Agencies',
          route: 'agencies',
          type: 'btn-side-nav',
          icon: 'briefcase',
        },
        {
          name: 'Agents',
          route: 'agents',
          type: 'btn-side-nav',
          icon: 'users',
        },
      ]
      this.addInspectionsIfEnabled();
    } else {
      this.navItems = [
        {
          name: 'Start a Quote',
          route: 'quote/new',
          type: 'btn-small',
          icon: 'dollar',
          class: 'black-outline',
          action: 'startQuote',
        },
        {
          name: 'Dashboard',
          route: 'dashboard',
          type: 'btn-side-nav',
          icon: 'table-columns',
        },
        {

          name: 'Submissions',
          route: 'submissions',
          type: 'btn-side-nav',
          icon: 'arrow-down-to-line',
        },
        {
          name: 'Policies',
          route: 'policies',
          type: 'btn-side-nav',
          icon: 'shield-check',
        },
        {
          name: 'Program Guidelines',
          route: 'program-guidelines',
          type: 'btn-side-nav',
          icon: 'list-check',
        },
        {
          name: 'Policy Forms',
          route: 'policy-forms',
          type: 'btn-side-nav',
          icon: 'table',
        }
      ]
    }
    if (this.authStore.user()?.roles?.includes('Wholesale Admin')) {
      this.navItems.push(
        {
          name: 'Wholesale Settings',
          route: 'wholesale-settings',
          type: 'btn-side-nav',
          icon: 'boxes-stacked',
        },
        {
          name: 'Agencies',
          route: 'agencies',
          type: 'btn-side-nav',
          icon: 'briefcase',
        },
      );
    }
    if (this.authStore.isWholesale()) {
      this.navItems.push(
        {
          name: 'Agents',
          route: 'agents',
          type: 'btn-side-nav',
          icon: 'users',
        },
      );
    }
  }
    
  // Inspections shows once any tenant has Coverage Validator on.
  private async addInspectionsIfEnabled() {
    if (!(await this.featureService.isFeatureEnabled(FeatureEnum.CoverageValidatorInspection))) {
      return;
    }
    const at = this.navItems.findIndex((i) => i.route === 'policies') + 1;
    this.navItems = [
      ...this.navItems.slice(0, at),
      { name: 'Inspections', route: 'inspections', type: 'btn-side-nav', icon: 'calendar-clock' },
      ...this.navItems.slice(at),
    ];
    this.cdr.markForCheck();
  }

  ngAfterViewInit() {
    this.notificationsCount = (this.notificationsStore.inAppNotifications() as any[])?.length;
  }

  onMouseEnter() {
    if (this.authStore.isAdmin() || this.authStore.isLossRunProcessor()) {
      this.imageSrc = this.logoSrc() || '/assets/branding/shasta-logo.png';
    } else {
      this.imageSrc = this.logoSrc() || '';
    }
  }

  onMouseLeave() {
    if (this.authStore.isAdmin() || this.authStore.isLossRunProcessor()) {
      this.imageSrc = this.iconSrc() || '/assets/branding/shasta-icon.png';
    } else {
      this.imageSrc = this.iconSrc() || '';
    }
  }

  openNotifcations() {
    this.modalService.open(NotificationsModalComponent, {
      modalType: ModalType.Half,
    });
  }

  handleItemClick(item: NavItems) {
    if (item.action === 'startQuote') {
      this.startQuote();
      return;
    }
    this.handleNavigation(item.route);
  }

  /**
   * Launches a quote for the current user. If the user's agency has access to a single program,
   * we go straight into that program's wizard. If they have more than one, we present a picker.
   * Program availability is driven by the same tiles that back the dashboard.
   */
  async startQuote() {
    let programs: { heading?: string | null; routingUrl?: string | null }[] = [];
    try {
      const result = (await this.dashboardService.getProgramTiles()) as unknown as ProgramTiles[];
      const sections = Array.isArray(result) ? result : [];
      programs = sections
        .flatMap((s) => s.programTiles ?? [])
        .filter((t) => !!t?.routingUrl)
        .map((t) => ({ heading: t.heading, routingUrl: t.routingUrl }));
    } catch {
      programs = [];
    }

    if (programs.length <= 1) {
      this.handleNavigation(programs[0]?.routingUrl ?? 'quote/new');
      return;
    }

    this.modalService.open(
      SelectProgramModalComponent,
      { modalType: ModalType.Small, data: { programs } },
      (routingUrl: string | null) => {
        if (routingUrl) {
          this.handleNavigation(routingUrl);
        }
      },
    );
  }

  handleNavigation(route: string) {
    // Starting a fresh quote must fully recreate the wizard component. Angular reuses the
    // existing instance when only the route param changes (e.g. quote/<id> -> quote/new), so
    // ngOnInit/ngOnDestroy never fire, the component-scoped WizardStore is never reset, and the
    // user lands in "edit mode" on their last quote. Bounce through '/' first to force a teardown
    // + rebuild. This covers every quote wizard (RCIP `quote/new` and LIO `lio-quote/new`); the
    // routingUrl comes from DB-driven tile JSON, so we can't match the literal 'quote/new'.
    const isNewQuote = route.split('?')[0].endsWith('quote/new');
    if (isNewQuote) {
      this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
        this.router.navigateByUrl(route);
      });
    } else {
      this.router.navigate([route]);
    }
  }
}
